import { supabase } from '@/foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '@/features/billValidation/types/billValidationTypes';
import { fromStoredRow, type IntervalDayRow, type StoredIntervalRow } from '@/features/billValidation/lib/intervalExport';

// NEM12 Interval Export. Stored intervals are read straight from
// bv_nem_interval (RLS scopes them to the caller's org); an uploaded file is
// parsed by the server, which writes nothing.

// PostgREST caps every response at 1000 rows, so pages must be exactly 1000.
const PAGE = 1000;
const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api/bill-validation/interval-export';

export type ParsedFile = {
  fileName: string;
  days: IntervalDayRow[];
  parseErrors: string[];
  validationErrors: string[];
};

export const intervalExportService = {
  /** Every channel-day for the site in `from`..`to` (YYYY-MM-DD, inclusive). */
  getStoredDays: async (siteId: string, from: string, to: string): Promise<ApiResponse<IntervalDayRow[]>> => {
    try {
      const out: IntervalDayRow[] = [];
      for (let start = 0; ; start += PAGE) {
        const { data, error } = await supabase
          .from('bv_nem_interval')
          .select('nmi, nmi_suffix, interval_date, interval_min, values, quality_flag, quality_events, uom, provider, updated_at')
          .eq('site_id', siteId)
          .gte('interval_date', from)
          .lte('interval_date', to)
          .order('interval_date', { ascending: true })
          .order('nmi_suffix', { ascending: true })
          .order('provider', { ascending: true })
          .range(start, start + PAGE - 1);
        if (error) throw error;
        for (const row of (data ?? []) as StoredIntervalRow[]) out.push(fromStoredRow(row));
        if (!data || data.length < PAGE) return { success: true, data: out };
      }
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Parse a NEM12 file to day-rows. No database write. */
  parseFile: async (file: File): Promise<ApiResponse<ParsedFile>> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const form = new FormData();
      form.append('nemFile', file);
      const res = await fetch(`${API_BASE}/parse`, {
        method: 'POST',
        headers: {
          'x-api-key': 'scopingai',
          ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: form,
      });
      const text = await res.text();
      let json: { success?: boolean; data?: unknown; error?: string };
      try { json = JSON.parse(text); } catch {
        return { success: false, error: `Request failed (${res.status}). The server did not return a valid response.` };
      }
      if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
      return { success: true, data: json.data as ParsedFile };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
